/**
 * @file useCompleteProfileForm.js
 * Custom hook for managing the complete profile form in the registration process.
 * It handles form state, validation, and submission logic.
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { trimAndCleanSpaces } from '../../../../utils/trimAndCleanSpaces';
import { PHONE_REGEX } from '../../../../utils/validations';
import { registerUser } from '../../../../app/store/slices/auth/authSlice';

export default function useCompleteProfileForm() {
    const dispatch = useDispatch();
    const { email } = useSelector((state) => state.auth);
    const [activeSlide, setActiveSlide] = React.useState('slide1');
    const [showPassword, setShowPassword] = React.useState(false);

    const initialValues = {
        name: '',
        dob: '',
        password: '',
        phone: '',
        isStepOneCompleted: false
    };

    const validationSchema = Yup.object({
        name: Yup.string().required('Name is required'),
        dob: Yup.string().required('Date of birth is required'),
        password: Yup.string().when('isStepOneCompleted', {
            is: true,
            then: (schema) =>
                schema
                    .required('Password is required')
                    .min(6, 'Password must be at least 6 characters'),
            otherwise: (schema) => schema.notRequired()
        }),
        phone: Yup.string().when('isStepOneCompleted', {
            is: true,
            then: (schema) =>
                schema
                    .matches(PHONE_REGEX, 'Must be a valid 10-digit phone number')
                    .required('Phone number is required'),
            otherwise: (schema) => schema.notRequired()
        })
    });

    const formik = useFormik({
        initialValues,
        validationSchema,
        validate: (values) => {
            const errors = {};

            // Validate user is at least 18 years old
            if (values.dob) {
                const dob = dayjs(values.dob, 'DD/MM/YYYY');
                if (dayjs().diff(dob, 'year') < 18) {
                    errors.dob = 'Age must be at least 18 years old';
                }
            }

            return errors;
        },
        onSubmit: async (values) => {
            try {
                // if name and dob are filled, then set the slide to slide2
                if (values.name && values.dob) {
                    setActiveSlide('slide2');
                }

                // structure the payload for registration
                const payload = {
                    name: trimAndCleanSpaces(values.name),
                    dob: dayjs(values.dob, 'YYYY/MM/DD').format('YYYY/MM/DD'),
                    password: values.password,
                    phone: values.phone,
                    email: email
                };

                // if user completed step one and filled password and phone, then dispatch the registerUser action
                if (values.isStepOneCompleted && values.password && values.phone) {
                    const response = await dispatch(registerUser(payload)).unwrap();

                    // if registration is successful, reset the form
                    if (response.status === true) {
                        formik.resetForm();
                    }
                }
            } catch (error) {
                if (error) {
                    Object.keys(error).forEach((field) => {
                        formik.setFieldError(field, error[field][0]);
                    });
                }
            }
        }
    });

    // to handle the slide changes
    function getSlideClass(slideName) {
        if (activeSlide === slideName) return 'slide-active';
        if (activeSlide === 'slide2' && slideName === 'slide1') return 'slide-left';
        if (activeSlide === 'slide1' && slideName === 'slide2') return 'slide-right';
        return 'slide-hidden';
    }

    return {
        getSlideClass,
        setActiveSlide,
        showPassword,
        setShowPassword,
        formik
    };
}
